import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSiteSettings } from '../services/templates';
import { applySeo } from '../utils/seo';
import type { HomepageSettings } from '../types';
import {
  DEFAULT_BRAND_FAVICON,
  PWA_APPLE_TOUCH_ICON,
  mergeHomepageSettings,
  resolveBrandFaviconUrl,
  type SiteBranding,
} from '../utils/siteBranding';
import {
  buildHomepageOgPool,
  ogBrandImageUrl,
  pickHomepageOgFromPool,
  appendOgVersion,
  resolveHomepageOgPool,
} from '../constants/ogBrand';

type SiteSeo = {
  siteTitle?: string;
  siteDescription?: string;
  keywords?: string[];
  defaultOgImageUrl?: string;
  twitterHandle?: string;
  faviconUrl?: string;
  sameAs?: string[];
};

type SiteBrandingContextValue = {
  branding: SiteBranding;
  siteSeo: SiteSeo | null;
  homepageSettings: HomepageSettings;
  loading: boolean;
  faviconUrl: string;
};

const SiteBrandingContext = createContext<SiteBrandingContextValue | null>(null);

const DEFAULT_SEO_DESCRIPTION =
  'Your all-in-one digital business platform — digital business cards, mini websites, and CRM.';

/** Paths that set their own OG meta — skip site-wide homepage OG rotation. */
const DEDICATED_OG_PATHS = new Set([
  '/miniwebsite',
  '/website',
  '/crm',
  '/crm-login',
  '/sales-crm',
  '/download',
  '/field-team-management',
  '/field-operations',
  '/sales-team-tracking',
  '/field-service-management',
  '/technician-tracking',
  '/security-workforce-management',
  '/supervisor-management',
  '/cleaning-workforce-management',
  '/field-workforce-management',
  '/crm-lead-management',
]);
const DEDICATED_OG_PREFIXES = ['/digital-card', '/templates', '/contact', '/crm-login'];

/** Paths that skip the site-settings network fetch (use defaults/cache only). */
const LIGHTWEIGHT_PATHS = new Set(['/download', '/faq', '/terms', '/privacy', '/refund-policy']);

function isDedicatedOgPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (DEDICATED_OG_PATHS.has(path)) return true;
  return DEDICATED_OG_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function absolutizeSeoUrl(url: string, origin: string): string {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return u.startsWith('/') ? `${origin}${u}` : `${origin}/${u}`;
}

function resolveHomepageOgImage(
  siteSeo: SiteSeo | null,
  homepageSettings: HomepageSettings,
  origin: string
): string {
  const s = siteSeo || {};
  const previews = (homepageSettings as HomepageSettings & { previews?: Record<string, unknown> })?.previews || {};
  if (s.defaultOgImageUrl) return appendOgVersion(absolutizeSeoUrl(s.defaultOgImageUrl, origin));
  const siteImage = typeof previews?.site === 'object' && previews.site && (previews.site as { imageUrl?: string }).imageUrl;
  if (siteImage) return appendOgVersion(absolutizeSeoUrl(String(siteImage), origin));
  const ogPool = resolveHomepageOgPool(homepageSettings as { previews?: { ogImages?: string[] } });
  if (ogPool.length) {
    const mode = String((previews as { ogRotationMode?: string }).ogRotationMode || 'daily');
    return pickHomepageOgFromPool(ogPool, mode, origin);
  }
  return ogBrandImageUrl(origin, 'site');
}

export function SiteBrandingProvider({ children }: { children: React.ReactNode }) {
  const [homepageSettings, setHomepageSettings] = useState<HomepageSettings>(() =>
    mergeHomepageSettings(null)
  );
  const [siteSeo, setSiteSeo] = useState<SiteSeo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/+$/, '') || '/';
      if (LIGHTWEIGHT_PATHS.has(path)) {
        setLoading(false);
        return;
      }
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getSiteSettings();
        if (cancelled) return;
        const hp = mergeHomepageSettings((data as { homepageSettings?: Record<string, unknown> })?.homepageSettings);
        setHomepageSettings(hp);
        setSiteSeo(((data as { siteSeo?: SiteSeo })?.siteSeo as SiteSeo) || null);
        try {
          localStorage.setItem(
            'homepage_server_cache',
            JSON.stringify({ at: Date.now(), homepageSettings: (data as { homepageSettings?: unknown })?.homepageSettings || {} })
          );
        } catch {
          /* ignore */
        }
      } catch {
        if (!cancelled) {
          try {
            const raw = localStorage.getItem('homepage_server_cache');
            if (raw) {
              const parsed = JSON.parse(raw);
              setHomepageSettings(mergeHomepageSettings(parsed?.homepageSettings));
            }
          } catch {
            /* ignore */
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const branding = homepageSettings.branding;
  const faviconUrl = resolveBrandFaviconUrl(branding, siteSeo);

  useEffect(() => {
    if (isDedicatedOgPath()) return;
    const s = siteSeo || {};
    const title = s.siteTitle || branding.siteName || 'Tcards';
    const description = s.siteDescription || DEFAULT_SEO_DESCRIPTION;
    const loc = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const favicon = faviconUrl.endsWith('.svg') ? faviconUrl : DEFAULT_BRAND_FAVICON;
    const image = resolveHomepageOgImage(siteSeo, homepageSettings, loc);
    const dispose = applySeo({
      title,
      description,
      keywords: Array.isArray(s.keywords) ? s.keywords : undefined,
      image,
      imageAlt: title,
      url: loc,
      siteName: title,
      twitterHandle: s.twitterHandle || undefined,
      canonicalUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      faviconUrl: favicon,
      appleTouchIconUrl: PWA_APPLE_TOUCH_ICON,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: title,
        url: typeof window !== 'undefined' ? window.location.origin : '',
        sameAs: Array.isArray(s.sameAs) ? s.sameAs : undefined,
        logo: image,
      },
    });
    return dispose;
  }, [siteSeo, branding.siteName, faviconUrl, homepageSettings]);

  const value = useMemo<SiteBrandingContextValue>(
    () => ({
      branding,
      siteSeo,
      homepageSettings,
      loading,
      faviconUrl,
    }),
    [branding, siteSeo, homepageSettings, loading, faviconUrl]
  );

  return <SiteBrandingContext.Provider value={value}>{children}</SiteBrandingContext.Provider>;
}

export function useSiteBranding(): SiteBrandingContextValue {
  const ctx = useContext(SiteBrandingContext);
  if (!ctx) {
    const merged = mergeHomepageSettings(null);
    return {
      branding: merged.branding,
      siteSeo: null,
      homepageSettings: merged,
      loading: false,
      faviconUrl: DEFAULT_BRAND_FAVICON,
    };
  }
  return ctx;
}

/** @deprecated Use useSiteBranding — kept for explicit default fallback naming */
export const useSiteBrandingWithDefaults = useSiteBranding;
